import React, { useState, useMemo, useEffect, useCallback } from 'react';
import * as XLSX from 'xlsx';
import {
  Wallet,
  ArrowUpRight,
  ArrowDownLeft,
  PieChart,
  Plus,
  MoreHorizontal,
  Receipt,
  TrendingUp,
  TrendingDown,
  CreditCard,
  DollarSign,
  Trash2,
  Settings,
  RotateCcw,
  Minus,
  LayoutList,
  BarChart3
} from 'lucide-react';
import { useModal } from "../contexts/ModalContext";
import { DEFAULT_THEME } from "../constants/theme";
import {
  FINANCE_CATEGORIES,
  FINANCE_ICONS,
  getFinanceCategoryColor,
} from "./finance/financeConstants";
import {
  formatDateLocal,
  getDaysInMonth,
  isSameDay,
  parseAmount,
} from "./finance/financeUtils";
import FinanceMobileHeaderActions from "./finance/FinanceMobileHeaderActions";
import FinancePortalTooltip from "./finance/FinancePortalTooltip";
import FinanceMobileSummaryCards from "./finance/FinanceMobileSummaryCards";
import FinanceMobileTransactionSection from "./finance/FinanceMobileTransactionSection";
import useFinanceTransactionFilters from "./finance/useFinanceTransactionFilters";
import useFinanceModalState from "./finance/useFinanceModalState";
import FinanceBudgetModal from "./finance/FinanceBudgetModal";
import FinanceExcelModal from "./finance/FinanceExcelModal";
import FinanceEditModal from "./finance/FinanceEditModal";
import FinanceDesktopToolbar from "./finance/FinanceDesktopToolbar";
import FinanceDesktopTransactionPanel from "./finance/FinanceDesktopTransactionPanel";
import MobileFloatingActionButton from "./common/MobileFloatingActionButton";

export default function FinanceView({ finances: transactions, annualBudget, onUpdateAnnualBudget, onAdd, onUpdate, onDelete, userProfile, selectedDepartment, theme = DEFAULT_THEME }) {
  // --- 상태 관리 ---
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // [NEW] Permission check - teachers can only view finance unless given permission
  const isTeacher = userProfile?.memberRole === 'teacher';
  const canManageFinance = userProfile?.canManageFinance === true;
  const readOnlyMode = isTeacher && !canManageFinance;

  // [NEW] 스와이프 제스처 상태
  const [touchStart, setTouchStart] = useState(null);
  const [touchEnd, setTouchEnd] = useState(null);
  const [touchStartY, setTouchStartY] = useState(null);
  const [touchEndY, setTouchEndY] = useState(null);
  const minSwipeDistance = 80;

  const onTouchStart = (e) => {
    setTouchEnd(null);
    setTouchEndY(null);
    setTouchStart(e.targetTouches[0].clientX);
    setTouchStartY(e.targetTouches[0].clientY);
  };

  const onTouchMove = (e) => {
    setTouchEnd(e.targetTouches[0].clientX);
    setTouchEndY(e.targetTouches[0].clientY);
  };

  const onCalendarSwipeEnd = () => {
    if (!touchStart || !touchEnd) return;
    const distance = touchStart - touchEnd;
    const isLeftSwipe = distance > minSwipeDistance;
    const isRightSwipe = distance < -minSwipeDistance;

    if (isLeftSwipe) {
      handleNextMonth(); // 다음 달로 이동
    } else if (isRightSwipe) {
      handlePrevMonth(); // 지난 달로 이동
    }
  };

  // [NEW] 위->아래 스와이프로 모달 닫기
  const onVerticalSwipeEnd = (closeHandler) => {
    if (!touchStartY || !touchEndY) return;
    const distance = touchEndY - touchStartY;
    const isDownSwipe = distance > minSwipeDistance;

    if (isDownSwipe) {
      closeHandler();
    }
  };
  const [isCalendarVisible, setIsCalendarVisible] = useState(true);

  // [NEW] On Mobile, default to Grid View (Hide Calendar)
  useEffect(() => {
    if (window.innerWidth < 768) setIsCalendarVisible(false);
  }, []);

  // [REMOVED] Local transactions state (using props as transactions)
  // [REMOVED] Local annualBudget state (using prop)

  const [isFlipped, setIsFlipped] = useState(false);
  const [excelDateRange, setExcelDateRange] = useState({ start: '', end: '' }); // [NEW] 엑셀 기간 선택
  const {
    isModalOpen,
    setIsModalOpen,
    showBudgetModal,
    setShowBudgetModal,
    showExcelModal,
    setShowExcelModal,
    editingItem,
    setEditingItem,
    formData,
    setFormData,
    modalPos,
    isDraggingRef,
    handleHeaderMouseDown,
    excelModalPos,
    handleExcelHeaderMouseDown,
  } = useFinanceModalState({ isMobile });

  // [FIX] Budget Input State Control
  const [budgetInput, setBudgetInput] = useState("");
  const [toast, setToast] = useState({ show: false, message: "" });

  const { openConfirm } = useModal ? useModal() : { openConfirm: (t, m, c) => WrapperConfirm(t, m, c) };

  // Church Name & Dept Name Logic
  const churchName = userProfile?.churchName || "우리교회";
  const deptName = userProfile?.deptName || "중고등부";

  useEffect(() => {
    localStorage.setItem('finance_transactions', JSON.stringify(transactions));
  }, [transactions]);

  // [NEW] Department Filtering
  const departmentFilteredTransactions = useMemo(() => {
    if (!selectedDepartment || selectedDepartment === 'All') return transactions;
    return transactions.filter(t => !t.department || t.department === 'All' || t.department === selectedDepartment);
  }, [transactions, selectedDepartment]);

  const {
    availableCategories,
    currentDate,
    currentDateStr,
    currentMonthTransactions,
    filteredTransactions,
    handleDateChange,
    handleNextMonth,
    handlePrevMonth,
    selectedCategory,
    selectedDate,
    setSelectedCategory,
    setSelectedDate,
  } = useFinanceTransactionFilters({ departmentFilteredTransactions });



  const showToast = (message) => {
    setToast({ show: true, message });
    setTimeout(() => setToast({ show: false, message: "" }), 3000);
  };

  // [NEW] 부서별 색상 매핑 (Refined with User Preference)
  const getDepartmentColor = (dept) => {
    if (!dept) return 'bg-gray-500';

    // 1. Check if it matches User's Main Department
    if (userProfile && (userProfile.deptName === dept || (dept.includes(userProfile.deptName))) && userProfile.deptColor) {
      return userProfile.deptColor;
    }

    if (dept.includes('유아')) return 'bg-pink-400';
    if (dept.includes('유치')) return 'bg-yellow-400';
    if (dept.includes('유년')) return 'bg-orange-400';
    if (dept.includes('초등')) return 'bg-sky-500';
    if (dept.includes('중등')) return 'bg-lime-500';
    if (dept.includes('고등')) return 'bg-teal-500';
    if (dept.includes('청년')) return 'bg-indigo-500';
    if (dept.includes('장년')) return 'bg-purple-600';
    return 'bg-gray-500'; // 그외
  };

  // [MOD] Resolve Budget based on Selected Department with Fallback
  const currentBudget = useMemo(() => {
    // Handle legacy number case (backward compatibility)
    if (typeof annualBudget === 'number') return annualBudget;

    if (!annualBudget) return 0;

    // 1. Try Selected Department specific budget (Exact Match)
    if (annualBudget[selectedDepartment]) {
      return annualBudget[selectedDepartment];
    }

    // 1.5 Try Flexible Match (Ignore Spaces) - [NEW]
    // "중고등부" vs "중고등부 " mismatch fix
    const normalize = (str) => str ? str.replace(/\s+/g, '') : '';
    const targetKey = Object.keys(annualBudget).find(key => normalize(key) === normalize(selectedDepartment));
    if (targetKey) {
      return annualBudget[targetKey];
    }

    // 2. Fallback to 'All' (Global Budget) 
    if (annualBudget['All']) {
      return annualBudget['All'];
    }

    return 0;
  }, [annualBudget, selectedDepartment]);

  const adjustBudget = (amount) => {
    const current = parseInt(budgetInput.replace(/,/g, '') || (currentBudget || 0), 10);
    setBudgetInput((current + amount).toLocaleString());
  };

  // [FIX] Sync budget input when annualBudget loads
  useEffect(() => {
    setBudgetInput(Number(currentBudget).toLocaleString());
  }, [currentBudget]);

  // ESC Key Listener
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'Escape') {
        setIsModalOpen(false);
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, []);

  // --- 통계 및 데이터 계산 ---
  const stats = useMemo(() => {
    const totalIncome = departmentFilteredTransactions
      .filter(t => t.type === '수입')
      .reduce((acc, curr) => acc + parseAmount(curr.amount), 0);

    const totalExpense = departmentFilteredTransactions
      .filter(t => t.type === '지출')
      .reduce((acc, curr) => acc + parseAmount(curr.amount), 0);

    // Monthly stats
    const currentMonth = currentDate.getMonth();
    const currentYear = currentDate.getFullYear();
    const monthlyTransactions = departmentFilteredTransactions.filter(t => {
      const d = new Date(t.date);
      return d.getMonth() === currentMonth && d.getFullYear() === currentYear;
    });

    const monthlyIncome = monthlyTransactions
      .filter(t => t.type === '수입')
      .reduce((acc, curr) => acc + parseAmount(curr.amount), 0);
    const monthlyExpense = monthlyTransactions
      .filter(t => t.type === '지출')
      .reduce((acc, curr) => acc + parseAmount(curr.amount), 0);

    return {
      totalBalance: totalIncome - totalExpense,
      totalIncome,
      totalExpense,
      monthlyIncome,
      monthlyExpense,
      monthlyBalance: monthlyIncome - monthlyExpense
    };
  }, [departmentFilteredTransactions, currentDate]);

  const topCategories = useMemo(() => {
    const currentMonth = currentDate.getMonth();
    const currentYear = currentDate.getFullYear();

    const expenseTransactions = departmentFilteredTransactions.filter(t => {
      const d = new Date(t.date);
      return t.type === '지출' && d.getMonth() === currentMonth && d.getFullYear() === currentYear;
    });

    const catMap = {};
    expenseTransactions.forEach(t => {
      const amount = parseAmount(t.amount);
      catMap[t.category] = (catMap[t.category] || 0) + amount;
    });

    return Object.entries(catMap)
      .sort((a, b) => b[1] - a[1])
      .slice(0, 3);
  }, [departmentFilteredTransactions, currentDate]);

  // [NEW] 엑셀 다운로드 기능 (기간 선택 지원)
  // 선택된 기간의 재정 내역을 엑셀 파일로 다운로드합니다.
  const exportToExcel = useCallback((preset = 'month') => {
    let startDate, endDate, periodLabel;
    const today = new Date();

    // 프리셋에 따른 기간 설정
    switch (preset) {
      case 'month': // 이번 달
        startDate = new Date(currentDate.getFullYear(), currentDate.getMonth(), 1);
        endDate = new Date(currentDate.getFullYear(), currentDate.getMonth() + 1, 0);
        periodLabel = `${currentDate.getFullYear()}년${currentDate.getMonth() + 1}월`;
        break;
      case '3months': // 최근 3개월
        startDate = new Date(today.getFullYear(), today.getMonth() - 2, 1);
        endDate = today;
        periodLabel = '최근3개월';
        break;
      case 'year': // 올해 전체
        startDate = new Date(today.getFullYear(), 0, 1);
        endDate = new Date(today.getFullYear(), 11, 31);
        periodLabel = `${today.getFullYear()}년`;
        break;
      case 'all': // 전체 내역
        startDate = null;
        endDate = null;
        periodLabel = '전체내역';
        break;
      case 'custom': // 직접 선택
        if (!excelDateRange.start || !excelDateRange.end) {
          showToast('시작일과 종료일을 선택해주세요.');
          return;
        }
        startDate = new Date(excelDateRange.start);
        endDate = new Date(excelDateRange.end);
        periodLabel = `${excelDateRange.start}~${excelDateRange.end}`;
        break;
      default:
        startDate = new Date(currentDate.getFullYear(), currentDate.getMonth(), 1);
        endDate = new Date(currentDate.getFullYear(), currentDate.getMonth() + 1, 0);
        periodLabel = `${currentDate.getFullYear()}년${currentDate.getMonth() + 1}월`;
    }

    // 기간에 맞는 데이터 필터링
    const filteredData = departmentFilteredTransactions.filter(t => {
      if (!startDate && !endDate) return true; // 전체 내역
      const tDate = new Date(t.date);
      return tDate >= startDate && tDate <= endDate;
    }).sort((a, b) => new Date(b.date) - new Date(a.date));

    if (filteredData.length === 0) {
      showToast('다운로드할 내역이 없습니다.');
      return;
    }

    // 통계 계산
    const totalIncome = filteredData.filter(t => t.type === '수입').reduce((acc, t) => acc + parseAmount(t.amount), 0);
    const totalExpense = filteredData.filter(t => t.type === '지출').reduce((acc, t) => acc + parseAmount(t.amount), 0);

    // 엑셀 데이터 준비
    const excelData = filteredData.map(t => ({
      '날짜': t.date,
      '구분': t.type,
      '카테고리': t.category,
      '금액': parseAmount(t.amount),
      '내용': t.description,
      '부서': t.department || '공통'
    }));

    // 수입/지출 합계 추가
    const summary = [
      {},
      { '날짜': '=== 요약 ===' },
      { '날짜': '총 수입', '금액': totalIncome },
      { '날짜': '총 지출', '금액': totalExpense },
      { '날짜': '잔액', '금액': totalIncome - totalExpense }
    ];

    // 워크시트 생성
    const ws = XLSX.utils.json_to_sheet([...excelData, ...summary]);

    // 열 너비 설정
    ws['!cols'] = [
      { wch: 12 }, // 날짜
      { wch: 8 },  // 구분
      { wch: 10 }, // 카테고리
      { wch: 15 }, // 금액
      { wch: 30 }, // 내용
      { wch: 12 }  // 부서
    ];

    // 워크북 생성
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, `재정내역`);

    // 파일명 생성 및 다운로드
    const fileName = `재정내역_${periodLabel}_${selectedDepartment === 'All' ? '전체부서' : selectedDepartment}.xlsx`;
    XLSX.writeFile(wb, fileName);

    setShowExcelModal(false);
    showToast('엑셀 파일이 다운로드되었습니다.');
  }, [departmentFilteredTransactions, currentDate, selectedDepartment, excelDateRange, showToast, parseAmount]);

  const handleDayClick = (date) => {
    if (date) setSelectedDate(date);
  };

  const handleDayDoubleClick = (date) => {
    if (date) {
      setSelectedDate(date);
      openAddModal(date);
    }
  };

  const openAddModal = (dateOverride = null, typeOverride = "지출") => {
    // [FIX] 권한 없는 경우 접근 차단
    if (readOnlyMode) {
      showToast("재정 입력 권한이 없습니다.");
      return;
    }
    setEditingItem(null);

    let targetDate = new Date();
    if (dateOverride instanceof Date) targetDate = dateOverride;
    else if (selectedDate) targetDate = selectedDate;

    setFormData({
      date: formatDateLocal(targetDate),
      type: typeOverride,
      category: "식비", // [FIX] 수입/지출 모두 기본값 식비
      amount: "",
      description: "",
      department: selectedDepartment // [NEW] 부서 자동 입력
    });
    setIsModalOpen(true);
  };

  const openEditModal = (item) => {
    // [FIX] 권한 없는 경우 접근 차단
    if (readOnlyMode) {
      showToast("재정 수정 권한이 없습니다.");
      return;
    }
    setEditingItem(item);
    setFormData({
      ...item,
      amount: Number(item.amount.toString().replace(/,/g, '')).toLocaleString()
    });
    setIsModalOpen(true);
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!formData.amount || !formData.description) return;

    const cleanFormData = {
      ...formData,
      amount: formData.amount.toString().replace(/,/g, '')
    };

    if (editingItem) {
      onUpdate({ ...cleanFormData, id: editingItem.id });
    } else {
      onAdd({ ...cleanFormData, id: Date.now() });
    }
    setIsModalOpen(false);
  };

  const handleDelete = (id) => {
    // [FIX] 권한 없는 경우 접근 차단
    if (readOnlyMode) {
      showToast("재정 삭제 권한이 없습니다.");
      return;
    }
    onDelete(id);
    setIsModalOpen(false);
  };

  const handleSaveBudget = (e) => {
    e.stopPropagation();
    if (budgetInput) {
      const newBudget = parseInt(budgetInput.replace(/,/g, ''), 10);
      if (!isNaN(newBudget)) {
        // [MOD] Save budget for current department
        onUpdateAnnualBudget(newBudget, selectedDepartment);
        showToast("예산이 성공적으로 저장되었습니다.");
      }
    }
  };

  // --- 날짜별 합계 계산 (달력 표시용) ---
  const dailyStats = useMemo(() => {
    const stats = {};
    const year = currentDate.getFullYear();
    const month = String(currentDate.getMonth() + 1).padStart(2, '0');
    const monthPrefix = `${year}-${month}`;

    departmentFilteredTransactions.filter(t => t.date.startsWith(monthPrefix)).forEach(t => {
      if (!stats[t.date]) stats[t.date] = { income: 0, expense: 0 };
      const amt = parseAmount(t.amount);
      if (t.type === '수입') stats[t.date].income += amt;
      else stats[t.date].expense += amt;
    });
    return stats;
  }, [departmentFilteredTransactions, currentDate]);




  // --- [NEW] 모바일 예산 관리 모달 ---
  const renderBudgetModal_Mobile = () => {
    return (
      <FinanceBudgetModal
        isOpen={showBudgetModal}
        onClose={() => setShowBudgetModal(false)}
        theme={theme}
        currentBudget={currentBudget}
        totalExpense={stats.totalExpense}
        budgetInput={budgetInput}
        setBudgetInput={setBudgetInput}
        onSave={handleSaveBudget}
      />
    );
  };

  // --- [NEW] 엑셀 다운로드 모달 ---
  // 기간 선택 프리셋과 직접 선택 옵션을 제공합니다.
  const renderExcelModal = () => {
    return (
      <FinanceExcelModal
        isOpen={showExcelModal}
        isMobile={isMobile}
        onClose={() => setShowExcelModal(false)}
        position={excelModalPos}
        onHeaderMouseDown={handleExcelHeaderMouseDown}
        theme={theme}
        currentDate={currentDate}
        excelDateRange={excelDateRange}
        setExcelDateRange={setExcelDateRange}
        exportToExcel={exportToExcel}
      />
    );
  };

  // --- [2] 등록/수정 모달 ---
  const renderEditModal = () => {
    return (
      <FinanceEditModal
        isOpen={isModalOpen}
        isMobile={isMobile}
        modalPos={modalPos}
        isDraggingRef={isDraggingRef}
        handleHeaderMouseDown={handleHeaderMouseDown}
        onClose={() => setIsModalOpen(false)}
        onTouchStart={onTouchStart}
        onTouchMove={onTouchMove}
        onTouchEndClose={() => onVerticalSwipeEnd(() => setIsModalOpen(false))}
        editingItem={editingItem}
        theme={theme}
        handleSubmit={handleSubmit}
        formData={formData}
        setFormData={setFormData}
        handleDelete={handleDelete}
      />
    );
  };

  // === [MOBILE UI RENDER] ===
  if (isMobile) {
    // 날짜 문자열 포맷팅 (YYYY-MM-DD for grouping display)
    const formatDateForGroup = (dateStr) => {
      const d = new Date(dateStr);
      const today = new Date();
      const isToday = d.toDateString() === today.toDateString();
      const days = ['일', '월', '화', '수', '목', '금', '토'];
      return isToday ? '오늘' : `${d.getMonth() + 1}월 ${d.getDate()}일 (${days[d.getDay()]})`;
    };

    return (
      <div
        className="bg-[#F0F0EB] min-h-screen pb-32"
        onTouchStart={onTouchStart}
        onTouchMove={onTouchMove}
        onTouchEnd={onCalendarSwipeEnd}
      >
        {/* Toast Notification */}
        {toast.show && (
          <div className={`fixed top-[calc(env(safe-area-inset-top,0px)+16px)] left-1/2 -translate-x-1/2 z-[9999] ${theme.bg} text-white px-6 py-3 rounded-full shadow-2xl flex items-center gap-3 animate-fade-in-down border border-[#f2d99d] ring-4 ring-[#f2d99d]/20`}>
            <div className={`bg-[#f2d99d] p-1.5 rounded-full ${theme.text.replace("text-", "text-")}`}><TrendingUp size={16} strokeWidth={3} /></div>
            <span className="font-bold text-sm tracking-wide">{toast.message}</span>
          </div>
        )}

        <FinanceMobileHeaderActions
          onOpenExcel={() => setShowExcelModal(true)}
          onPrevMonth={handlePrevMonth}
          onNextMonth={handleNextMonth}
          currentDate={currentDate}
          currentDateStr={currentDateStr}
          onChangeMonth={handleDateChange}
          theme={theme}
        />

        {/* Content */}
        <div className="px-4 space-y-4">
          <FinanceMobileSummaryCards
            currentBudget={currentBudget}
            isTeacher={isTeacher}
            onOpenBudget={() => setShowBudgetModal(true)}
            stats={stats}
          />

          {/* 3. Transaction List (Grouped by Date) */}
          <div className="space-y-4 pt-2">
            <FinanceMobileTransactionSection
              filteredTransactions={filteredTransactions}
              formatDateForGroup={formatDateForGroup}
              onEdit={openEditModal}
              readOnlyMode={readOnlyMode}
            />
          </div>
        </div>

        {renderEditModal()}
        {renderBudgetModal_Mobile()}
        {renderExcelModal()}

        {!readOnlyMode && (
          <MobileFloatingActionButton
            onClick={openAddModal}
            className={`${theme.bg} text-white`}
            ariaLabel="재정 항목 추가"
            title="재정 항목 추가"
          >
            <Plus size={28} strokeWidth={2.5} />
          </MobileFloatingActionButton>
        )}
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto min-h-screen flex flex-col gap-6 pb-6 px-4 pt-2 relative">
      {/* Toast Notification */}
      {toast.show && (
        <div className={`absolute top-[calc(env(safe-area-inset-top,0px)+16px)] left-1/2 -translate-x-1/2 z-[9999] ${theme.bg} text-white px-6 py-3 rounded-full shadow-2xl flex items-center gap-3 animate-fade-in-down border border-[#f2d99d] ring-4 ring-[#f2d99d]/20`}>
          <div className={`bg-[#f2d99d] p-1.5 rounded-full ${theme.text.replace("text-", "text-")}`}><TrendingUp size={16} strokeWidth={3} /></div>
          <span className="font-bold text-sm tracking-wide">{toast.message}</span>
        </div>
      )}

      {/* Custom Styles for hiding number input spinners */}
      <style>{`
        input[type=number]::-webkit-inner-spin-button, 
        input[type=number]::-webkit-outer-spin-button { 
          -webkit-appearance: none; 
          margin: 0; 
        }
        input[type=number] {
          -moz-appearance: textfield;
        }
      `}</style>


      {/* [0] 타이틀 헤더 (Header Section) */}
      <div className="hidden md:flex flex-col md:flex-row justify-between items-end gap-6">
        <div>
          <h2 className={`text-3xl font-black text-gray-800 flex items-center gap-3 font-do-hyeon`}>
            <img src={FINANCE_ICONS.wallet} width={48} alt="icon" className="drop-shadow-md" /> 재정 관리
          </h2>
          <p className="text-gray-500 text-sm font-medium mt-1 ml-1">투명하고 정확하게 교회 재정을 관리하세요.</p>
        </div>
      </div>

      {/* 상단 섹션: 좌(월렛), 우(현황+Top3+버튼) */}
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 min-h-[280px]">

        {/* [LEFT COLUMN] Wallet (Full Height, Click Flip) */}
        <div className="lg:col-span-6 xl:col-span-6 flex flex-col h-full">

          {/* 3D Flip Wallet Card (Click Controlled) */}
          <div className="perspective-1000 group w-full h-full min-h-[260px]">
            <div className={`relative w-full h-full transition-transform duration-700 transform-style-3d ${isFlipped ? 'rotate-y-180' : ''}`}>

              {/* [FRONT] Original Wallet Design */}
              <div
                className={`absolute inset-0 backface-hidden rounded-[2.5rem] p-7 text-white shadow-2xl overflow-hidden bg-gradient-to-br from-[#2C3E30] to-[#1a2e20] border border-white/10 flex flex-col justify-between hover:shadow-3xl transition-shadow duration-300 ${isFlipped ? 'pointer-events-none' : ''}`}
                onClick={() => setIsFlipped(true)}
              >
                <div className="absolute inset-0 opacity-30 bg-[url('https://www.transparenttextures.com/patterns/cubes.png')]"></div>
                <div className="absolute -right-20 -top-20 w-80 h-80 bg-[#354F38] rounded-full blur-3xl opacity-50"></div>

                <div className="relative z-10 flex justify-between items-start">
                  <div className="flex items-center gap-2 opacity-80">
                    <Wallet size={24} />
                    <span className="text-sm font-bold tracking-widest">MINISTRY WALLET</span>
                  </div>
                  {/* IC Chip Detail */}
                  <div className="w-16 h-11 bg-gradient-to-br from-[#f2d99d] via-[#fdfbf7] to-[#d4af6d] rounded-xl shadow-lg relative overflow-hidden border border-[#b89355] flex items-center justify-center">
                    <div className="absolute top-1/2 left-0 w-full h-[1px] bg-[#9e7b45] opacity-40"></div>
                    <div className="absolute left-1/2 top-0 w-[1px] h-full bg-[#9e7b45] opacity-40"></div>
                    <div className="absolute top-[25%] left-0 w-full h-[1px] bg-[#9e7b45] opacity-20"></div>
                    <div className="absolute top-[75%] left-0 w-full h-[1px] bg-[#9e7b45] opacity-20"></div>
                    <div className="absolute left-[30%] top-0 w-[1px] h-full bg-[#9e7b45] opacity-20"></div>
                    <div className="absolute left-[70%] top-0 w-[1px] h-full bg-[#9e7b45] opacity-20"></div>
                    <div className="w-6 h-4 border border-[#9e7b45] opacity-30 rounded-md"></div>
                  </div>
                </div>

                <div className="relative z-10 space-y-2 text-center my-auto">
                  <span className="text-sm font-medium opacity-60 flex items-center justify-center">현재 잔액 (Current Balance) <FinancePortalTooltip text={`연간 예산 + 전체 수입 - 전체 지출\n(실제 가용 잔액)`} theme={theme} /></span>
                  <h1 className="text-5xl font-black font-do-hyeon tracking-tight drop-shadow-lg">
                    {(currentBudget + stats.totalBalance).toLocaleString()}<span className="text-3xl ml-2">원</span>
                  </h1>
                </div>

                <div className="relative z-10 flex justify-between items-end">
                  <div>
                    <h3 className="text-xl font-bold">{churchName} {selectedDepartment === 'All' ? '전체 부서' : selectedDepartment}</h3>
                    <p className="text-xs opacity-60 font-mono mt-1">**** **** 8291</p>
                  </div>
                  <div className="flex items-center gap-1 opacity-60 text-xs bg-white/10 px-3 py-1.5 rounded-full">
                    <RotateCcw size={14} />
                    <span>Click to Flip</span>
                  </div>
                </div>
              </div>

              {/* [BACK] Settings & Budget Info - 교사 권한일 경우 수정 불가 */}
              <div
                className={`absolute inset-0 backface-hidden rotate-y-180 rounded-[2.5rem] p-7 text-white shadow-2xl overflow-hidden bg-[#354F38] border border-[#48634b] flex flex-col justify-between cursor-pointer ${isFlipped ? 'pointer-events-auto z-50' : 'pointer-events-none'}`}
                onClick={() => setIsFlipped(false)}
              >
                <div className="flex justify-between items-center z-10 relative">
                  <h3 className="font-bold flex items-center gap-2 text-2xl"><Settings size={22} /> 예산 {isTeacher ? '정보' : '설정'}</h3>
                  <button
                    onClick={(e) => { e.stopPropagation(); setIsFlipped(false); }}
                    className="bg-white/10 hover:bg-white/20 p-2 rounded-full transition transform hover:rotate-180 duration-500 z-50 pointer-events-auto"
                  >
                    <RotateCcw size={20} />
                  </button>
                </div>

                <div className="space-y-6 z-10 relative flex-1 flex flex-col justify-center">
                  <div onClick={e => e.stopPropagation()} className="cursor-default">
                    <label className="text-sm opacity-60 font-bold block mb-2">{selectedDepartment === 'All' ? '전체' : selectedDepartment} 예산 (Annual Budget)</label>

                    {/* [MOD] 교사 권한일 경우 읽기 전용으로 표시 */}
                    {isTeacher ? (
                      <div className="flex items-center gap-3 border-b-2 border-white/20 pb-2 mx-2">
                        <span className="font-black text-2xl">₩</span>
                        <span className="text-3xl font-black">{Number(currentBudget).toLocaleString()}</span>
                      </div>
                    ) : (
                      <>
                        <div className="flex items-center gap-2">
                          <div className="flex-1 flex items-center gap-3 border-b-2 border-white/20 pb-2 mx-2">
                            <span className="font-black text-2xl">₩</span>
                            <input
                              type="text"
                              className="bg-transparent text-3xl font-black outline-none w-full placeholder-white/30 relative z-50"
                              value={budgetInput}
                              onClick={(e) => { e.stopPropagation(); e.target.focus(); }}
                              onMouseDown={(e) => e.stopPropagation()}
                              onChange={(e) => {
                                const val = e.target.value.replace(/[^0-9]/g, '');
                                setBudgetInput(val ? Number(val).toLocaleString() : '');
                              }}
                              placeholder="예산 입력"
                            />
                          </div>

                          {/* [NEW] Buttons Combined */}
                          <div className="flex gap-1">
                            <button onClick={(e) => { e.stopPropagation(); adjustBudget(-100000); }} className="p-3 bg-white/10 hover:bg-white/20 rounded-xl transition hover:scale-105 active:scale-95"><Minus size={18} /></button>
                            <button onClick={(e) => { e.stopPropagation(); adjustBudget(100000); }} className="p-3 bg-white/10 hover:bg-white/20 rounded-xl transition hover:scale-105 active:scale-95"><Plus size={18} /></button>
                          </div>
                        </div>

                        <div className="flex justify-between items-center mt-4">
                          {/* Reset Button */}
                          <button
                            onClick={(e) => { e.stopPropagation(); setBudgetInput('0'); }}
                            className="text-xs text-white/50 hover:text-white flex items-center gap-1 transition px-2 py-1 hover:bg-white/10 rounded-lg"
                          >
                            <RotateCcw size={14} /> 초기화
                          </button>

                          {/* Save Button */}
                          <button onClick={handleSaveBudget} className={`text-sm bg-[#f2d99d] ${theme.text} px-6 py-2.5 rounded-xl font-black hover:bg-white shadow-lg transform hover:scale-105 transition flex items-center gap-2`}>
                            <Receipt size={16} /> 저장하기
                          </button>
                        </div>
                      </>
                    )}
                  </div>

                  <div>
                    <div className="flex justify-between text-sm font-bold opacity-80 mb-2">
                      <span>예산 소진율</span>
                      <span>{Math.min((stats.totalExpense / (currentBudget || 1)) * 100, 100).toFixed(1)}%</span>
                    </div>
                    <div className="w-full h-4 bg-black/20 rounded-full overflow-hidden">
                      <div className="h-full bg-white transition-all duration-1000" style={{ width: `${Math.min((stats.totalExpense / (currentBudget || 1)) * 100, 100)}%` }}></div>
                    </div>
                    <div className="flex justify-between text-xs mt-2 opacity-60">
                      <span>총 지출: {stats.totalExpense.toLocaleString()}</span>
                      <span>남은 예산: {(currentBudget - stats.totalExpense).toLocaleString()}</span>
                    </div>
                  </div>
                </div>
                <div className="opacity-10 absolute -bottom-10 -right-10"><Wallet size={200} /></div>
              </div>
            </div>
          </div>
        </div>

        {/* [RIGHT COLUMN] Monthly Analysis (Chart + Top3) + Buttons */}
        <div className="lg:col-span-6 xl:col-span-6 flex flex-col gap-4">

          {/* 1. Monthly Chart */}
          <div className="bg-white/60 backdrop-blur-xl border border-white/40 rounded-[2rem] p-4 flex flex-col justify-center shadow-sm h-28">
            <div className="flex justify-between items-center mb-2">
              <h3 className={`font-black text-lg text-gray-800`}>이번 달 현황</h3>
              <span className={`text-sm font-black ${stats.monthlyBalance >= 0 ? 'text-blue-600' : 'text-rose-600'}`}>
                {stats.monthlyBalance >= 0 ? '+' : ''}{stats.monthlyBalance.toLocaleString()}원
              </span>
            </div>
            {/* Progress Bar */}
            <div className="space-y-2">
              <div className="w-full h-5 bg-gray-100 rounded-full overflow-hidden flex shadow-inner">
                <div className="h-full bg-blue-400" style={{ width: `${(stats.monthlyIncome / (stats.monthlyIncome + stats.monthlyExpense || 1)) * 100}%` }}></div>
                <div className="h-full bg-rose-400" style={{ flex: 1 }}></div>
              </div>
              <div className="flex justify-between text-[10px] font-bold text-gray-400 px-1">
                <span className="text-blue-500">수입 {stats.monthlyIncome.toLocaleString()}</span>
                <span className="text-rose-500">지출 {stats.monthlyExpense.toLocaleString()}</span>
              </div>
            </div>
          </div>

          {/* Grid Container for Top 3 and Buttons */}
          <div className="grid grid-cols-3 gap-4 h-[260px]">

            {/* 2. Top 3 Widget (Left 2/3) */}
            <div className="col-span-2 bg-white/60 backdrop-blur-xl p-5 rounded-[2rem] shadow-sm border border-white/40 flex flex-col h-full">
              <h3 className="text-base font-black text-gray-800 mb-2 flex items-center gap-2">
                <img src={FINANCE_ICONS.chart} width={20} alt="chart" />
                이번 달 지출 TOP 3
              </h3>

              {topCategories.length === 0 ? (
                <div className="flex-1 flex items-center justify-center text-gray-400 text-xs font-bold">내역 없음</div>
              ) : (
                <div className="space-y-2 overflow-y-auto custom-scrollbar pr-1 flex-1">
                  {topCategories.map(([cat, amount], idx) => (
                    <div key={idx} className="flex items-center gap-3 bg-white p-2.5 rounded-xl shadow-sm border border-gray-50">
                      <div className={`w-8 h-8 rounded-lg flex items-center justify-center text-sm ${idx === 0 ? 'bg-orange-50 text-orange-500' : 'bg-gray-50 text-gray-500'}`}>
                        <span className="font-black font-do-hyeon">{idx + 1}</span>
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="font-bold text-gray-800 text-xs">{cat}</div>
                        <div className="text-[10px] text-gray-400 font-medium">{amount.toLocaleString()}원</div>
                      </div>
                      <div className={`w-1 h-6 rounded-full ${getFinanceCategoryColor(cat)}`}></div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* 3. Weekly Spending Trend (Right 1/3, Single Unified Card) */}
            <div className="col-span-1 bg-white/60 backdrop-blur-xl p-5 rounded-[2rem] shadow-sm border border-white/40 flex flex-col h-full relative overflow-hidden group">
              {/* Background Decoration */}
              <div className="absolute top-0 right-0 w-32 h-32 bg-rose-100/50 rounded-full blur-3xl -mr-10 -mt-10 pointer-events-none"></div>

              <div className="relative z-10 flex flex-col h-full">
                <h3 className="text-base font-black text-gray-800 mb-4 flex items-center gap-2">
                  <BarChart3 size={20} className="text-rose-500" />
                  주간 지출 추이
                </h3>

                {/* Chart Area */}
                <div className="flex-1 flex items-stretch justify-between gap-2 mt-6">
                  {(() => {
                    // Weekly Data Calculation
                    const weeklyData = [0, 0, 0, 0, 0]; // Week 1 to 5
                    const today = new Date();
                    const currentWeekIndex = Math.ceil(today.getDate() / 7) - 1;

                    // Filter current month expenses and group by week
                    currentMonthTransactions
                      .filter(t => t.type === '지출')
                      .forEach(t => {
                        if (!t.date) return;
                        const d = new Date(t.date);
                        // Simple 1-7, 8-14... week calculation
                        const weekIdx = Math.ceil(d.getDate() / 7) - 1;
                        if (weekIdx >= 0 && weekIdx < 5) {
                          weeklyData[weekIdx] += t.amount;
                        }
                      });

                    const maxVal = Math.max(...weeklyData, 1); // Avoid div by zero

                    return weeklyData.map((amount, idx) => {
                      const isCurrentWeek = idx === currentWeekIndex;
                      const heightPercent = Math.max((amount / maxVal) * 100, 5); // Min 5% height

                      return (
                        <div key={idx} className="flex-1 flex flex-col justify-end items-center gap-2 group/bar relative h-full">
                          {/* Value Tooltip (Fixed at Top of Container) */}
                          <div className="opacity-0 group-hover/bar:opacity-100 absolute top-0 bg-gray-800 text-white text-[10px] py-1 px-2 rounded-lg transition-opacity whitespace-nowrap z-20 pointer-events-none shadow-lg transform -translate-y-full mb-1">
                            {amount.toLocaleString()}
                          </div>

                          {/* Bar */}
                          <div className="w-full relative flex items-end justify-center h-24 rounded-t-xl bg-gray-100/50 overflow-hidden">
                            <div
                              className={`w-full rounded-t-xl transition-all duration-700 ease-out relative ${isCurrentWeek ? 'bg-gradient-to-t from-rose-300 to-rose-500 shadow-md' : 'bg-gray-200 hover:bg-gray-300'}`}
                              style={{ height: `${heightPercent}%` }}
                            >
                              {/* Highlight Dot for Max Value */}
                              {amount === maxVal && amount > 0 && (
                                <div className="absolute -top-1 left-1/2 -translate-x-1/2 w-2 h-2 bg-rose-500 rounded-full ring-2 ring-white"></div>
                              )}
                            </div>
                          </div>

                          {/* Label */}
                          <span className={`text-[10px] font-bold ${isCurrentWeek ? 'text-rose-600' : 'text-gray-400'}`}>
                            {idx + 1}주
                          </span>
                        </div>
                      );
                    });
                  })()}
                </div>
              </div>


            </div>
          </div>
        </div>
      </div>


      <FinanceDesktopToolbar
        theme={theme}
        currentDate={currentDate}
        currentDateStr={currentDateStr}
        handleDateChange={handleDateChange}
        handlePrevMonth={handlePrevMonth}
        handleNextMonth={handleNextMonth}
        isCalendarVisible={isCalendarVisible}
        setIsCalendarVisible={setIsCalendarVisible}
        readOnlyMode={readOnlyMode}
        openAddModal={openAddModal}
        onOpenExcel={() => setShowExcelModal(true)}
      />

      {/* Calendar Container */}
      <div className="flex flex-col gap-6 flex-1 min-h-0">
        {/* 캘린더 + 사이드바 */}
        <div className={`flex-1 grid gap-6 min-h-0 transition-all duration-500 ${isCalendarVisible ? 'grid-cols-1 lg:grid-cols-[1fr_380px]' : 'grid-cols-1'}`}>

          {/* [좌측] 대형 캘린더 */}
          <div className={`bg-white/60 backdrop-blur-xl p-6 rounded-[2.5rem] shadow-sm border border-white/40 flex flex-col h-full transition-all duration-500 overflow-x-auto ${isCalendarVisible ? 'opacity-100 translate-x-0' : 'hidden opacity-0 -translate-x-10'}`}>
            {/* ... Calendar Content ... */}
            <div className="min-w-[600px]">
              <div className="grid grid-cols-7 mb-4 text-center">
                {['일', '월', '화', '수', '목', '금', '토'].map((day, i) => (
                  <div key={day} className={`text-sm font-black uppercase tracking-wider ${i === 0 ? 'text-rose-500' : i === 6 ? 'text-blue-500' : 'text-gray-400'}`}>{day}</div>
                ))}
              </div>

              <div className="grid grid-cols-7 gap-3 flex-1 min-h-[400px] auto-rows-fr" style={{ gridAutoRows: '1fr' }}>
                {getDaysInMonth(currentDate).map((date, idx) => {
                  if (!date) return <div key={idx}></div>;

                  const isSelected = isSameDay(date, selectedDate);
                  const isToday = isSameDay(date, new Date());
                  const dateStr = formatDateLocal(date);

                  const dayData = dailyStats[dateStr] || { income: 0, expense: 0 };
                  const hasIncome = dayData.income > 0;
                  const hasExpense = dayData.expense > 0;

                  const dayOfWeek = date.getDay();
                  const isSunday = dayOfWeek === 0;
                  const isSaturday = dayOfWeek === 6;

                  return (
                    <div
                      key={idx}
                      onClick={() => handleDayClick(date)}
                      onDoubleClick={() => handleDayDoubleClick(date)}
                      className={`
                                        min-h-[90px]
                                        rounded-[1.5rem] flex flex-col items-start justify-start p-2 cursor-pointer transition-all duration-300 relative border group
                                        ${isSelected
                          ? `bg-white ${theme.border} ring-4 ${theme.border.replace('border-', 'ring-').replace(']', ']/10')} shadow-lg z-10 scale-[1.02]`
                          : isToday
                            ? "bg-white/80 border-gray-200 shadow-inner"
                            : "bg-white/40 border-transparent hover:bg-white hover:shadow-md hover:-translate-y-1"
                        }
                                    `}
                    >
                      <div className="flex justify-between items-start w-full relative z-10">
                        <span className={`text-sm font-bold ml-1 mt-1 w-7 h-7 flex items-center justify-center rounded-full 
                                            ${isToday ? `${theme.bg} text-white shadow-md` : isSelected ? `${theme.text}` : isSunday ? 'text-rose-500' : isSaturday ? 'text-blue-500' : 'text-gray-600'}`}>
                          {date.getDate()}
                        </span>
                      </div>

                      {/* Hover Add Overlay */}
                      <div className="absolute inset-0 bg-black/5 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity duration-200 z-0 rounded-[1.5rem]">
                        <Plus size={24} className="text-gray-400/50" />
                      </div>

                      {/* 수입/지출 뱃지 */}
                      <div className="flex flex-col gap-1 mt-auto w-full px-1 mb-1 relative z-10">
                        {hasIncome && (
                          <div className="text-[10px] truncate px-2 py-1 rounded-lg font-bold flex items-center gap-1 shadow-sm bg-blue-50 text-blue-600">
                            <ArrowDownLeft size={10} className="flex-shrink-0" />
                            <span className="truncate">+{dayData.income.toLocaleString()}</span>
                          </div>
                        )}
                        {hasExpense && (
                          <div className="text-[10px] truncate px-2 py-1 rounded-lg font-bold flex items-center gap-1 shadow-sm bg-rose-50 text-rose-600">
                            <ArrowUpRight size={10} className="flex-shrink-0" />
                            <span className="truncate">-{dayData.expense.toLocaleString()}</span>
                          </div>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>

          {/* [우측] 스마트 사이드바 */}
          <div className={`flex flex-col gap-6 min-h-0 pr-1 ${isCalendarVisible ? 'h-full overflow-y-auto custom-scrollbar' : ''}`}>

            <FinanceDesktopTransactionPanel
              selectedDate={selectedDate}
              setSelectedDate={setSelectedDate}
              availableCategories={availableCategories}
              selectedCategory={selectedCategory}
              setSelectedCategory={setSelectedCategory}
              filteredTransactions={filteredTransactions}
              isCalendarVisible={isCalendarVisible}
              getDepartmentColor={getDepartmentColor}
              onEdit={openEditModal}
              readOnlyMode={readOnlyMode}
              selectedDepartment={selectedDepartment}
            />

          </div>
        </div>
      </div>

      {renderEditModal()}
      {renderExcelModal()}
    </div>
  );
}
